"use client";

import { c as _c } from "react/compiler-runtime";
import { useLayoutEffect, useRef, type ReactNode } from "react";
import { usePathname } from "next/navigation";
import { DESKTOP_QUERY } from "@/lib/breakpoints";
type HeadingRevealModule = typeof import("./headingReveal");

// GSAP + ScrollTrigger + SplitText (~200 KB) only ever run at desktop width,
// so they're loaded on demand instead of shipping in every page's bundle.
// On a desktop viewport the fetch starts as soon as this module evaluates,
// in parallel with hydration, so it's ready by the time the effect runs.
let headingRevealPromise: Promise<HeadingRevealModule> | null = null;
const loadHeadingReveal = () => headingRevealPromise ??= import("./headingReveal");
if (typeof window !== "undefined" && window.matchMedia(DESKTOP_QUERY).matches) {
  void loadHeadingReveal();
}
type HomeTextAnimationsProps = {
  children: ReactNode;
};
export default function HomeTextAnimations(t0) {
  const $ = _c(5);
  const {
    children
  } = t0;
  const scopeRef = useRef(null);
  const pathname = usePathname();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const scope = scopeRef.current;
      if (!scope) {
        return;
      }
      scope.dataset.textAnimations = "pending";
      let cancelled = false;
      let teardown;
      const desktop = window.matchMedia(DESKTOP_QUERY);
      const start = () => {
        desktop.removeEventListener("change", onChange);
        loadHeadingReveal().then(t2 => {
          const {
            setupHeadingReveal
          } = t2;
          if (cancelled) {
            return;
          }
          teardown = setupHeadingReveal(scope);
        }).catch(_temp).finally(() => {
          if (!cancelled) {
            scope.dataset.textAnimations = "ready";
          }
        });
      };
      const onChange = () => {
        if (desktop.matches) {
          start();
        }
      };
      if (desktop.matches) {
        start();
      } else {
        scope.dataset.textAnimations = "ready";
        desktop.addEventListener("change", onChange);
      }
      return () => {
        cancelled = true;
        desktop.removeEventListener("change", onChange);
        teardown?.();
        scope.dataset.textAnimations = "pending";
        scope.querySelectorAll("[data-text-reveal]").forEach(_temp2);
      };
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== pathname) {
    t2 = [pathname];
    $[1] = pathname;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useLayoutEffect(t1, t2);
  let t3;
  if ($[3] !== children) {
    t3 = <div ref={scopeRef} className="contents" data-text-animations="pending">{children}</div>;
    $[3] = children;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
function _temp2(element) {
  return delete element.dataset.textReveal;
}
function _temp() {}